import { Injectable } from '@angular/core';
import { GameStore } from './game.store';
import { Game } from './models/game.model';
import { tap, mapTo } from 'rxjs/operators';
import { ApiService } from '../shared/services/core/api.service';
import { Observable, of } from 'rxjs';
import { GameDataSource } from './models/game-data-source.model';
import { cacheable, guid } from '@datorama/akita';
import { GameQuery } from './game.query';
import { CustomerTicketSummary } from './models/customer-ticket-summary.model';
import { GameTicketStore } from './game-ticket.store';

@Injectable({ providedIn: 'root' })
export class GameService {
  constructor(private gameStore: GameStore, private apiService: ApiService, private gameQuery: GameQuery, private gameTicketStore: GameTicketStore) {}

  /**
   * Will fetch the game data depending
   * on the given game data source.
   * The game data will be added to the store
   * and set each games data source type.
   * If no data source passed, this will default
   * to member ID game data.
   * @param dataSource type of game data
   * @param startDate start date of data
   * @returns request to get data and add to store
   * or of(null) if data exists
   */
  fetchGames(startDate: string = '', dataSource: GameDataSource = GameDataSource.MemberId) {
    let url = this.generateUrl(startDate, dataSource);
    const request$: Observable<null> = this.apiService.get<Game>(url).pipe(
      tap((games) => {
        games = this.setGameDataSource(games, dataSource);
        this.gameStore.add(games);
      }),
      mapTo(null)
    );

    return this.gameDataExists(dataSource) ? of(null) : request$;
  }

  /**
   * This will generate the endpoint
   * depending on the game data source passed.
   * @param startDate
   * @param dataSource
   * @returns the url generated
   */
  private generateUrl(startDate: string, dataSource: GameDataSource): string {
    let url: string = `events?startDate=${startDate}`;

    switch (dataSource) {
      case GameDataSource.Club:
        url = `${url}&dataSource=club`;
        break;
      case GameDataSource.ExternalRef:
        url = `${url}&dataSource=externalref`;
        break;
      case GameDataSource.MemberId:
        url = `${url}&dataSource=memberid`;
        break;
      default:
        break;
    }

    return url;
  }

  /**
   * This will set the data source
   * type of each game and the
   * random ID of the game.
   * @param games
   * @param dataSource
   * @returns This will return an array of games
   * which will have their random ID's set
   * and the data source type.
   */
  private setGameDataSource(games: Game[], dataSource: GameDataSource): Game[] {
    games.forEach((game) => {
      game.id = guid();

      switch (dataSource) {
        case GameDataSource.Club:
          game.dataSource = GameDataSource.Club;
          break;
        case GameDataSource.ExternalRef:
          game.dataSource = GameDataSource.ExternalRef;
          break;
        case GameDataSource.MemberId:
          game.dataSource = GameDataSource.MemberId;
          break;
        default:
          game.dataSource = GameDataSource.MemberId;
          break;
      }
    });

    return games;
  }

  /**
   * Check to see if game data of
   * given data source type
   * already exists within the
   * games store.
   * @param dataSourceType
   * @returns
   */
  gameDataExists(dataSourceType: GameDataSource): boolean {
    let dataExists = false;

    let games = this.gameQuery.getAll({ filterBy: ({ dataSource }) => dataSource === dataSourceType });
    if (games.length > 0) {
      dataExists = true;
    }

    return dataExists;
  }

  /**
   * Will fetch data if not cached but will also
   * set a game as active, for cases where
   * game banner is used in different places (which
   * depends on a acive game entity).
   * This will be called on parent component.
   * @param gameNumber game number of game
   * @param dataSourceType type of game data
   */
  fetchGamesAndSetActiveGame(gameNumber: number, dataSourceType: GameDataSource) {
    this.fetchGames('', dataSourceType)
      .toPromise()
      .then(() => {
        this.gameQuery
          .selectGameByDataSourceAndGameNumber(dataSourceType, gameNumber)
          .subscribe((game) => {
            if (!!game) {
              this.setActiveGame(game);
            }
          })
          .unsubscribe();
      });
  }

  /**
   * Set game entity as active.
   * @param game game to be active.
   */
  setActiveGame(game: Game) {
    this.gameStore.setActive(game.id);
  }

  fetchGamesWithTickets() {
    const request$ = this.apiService.get<CustomerTicketSummary>('events/eventsandtickets').pipe(
      tap(games => this.gameTicketStore.set(games)
    ));
    
    return cacheable(this.gameTicketStore, request$);
  }

}
